/* Medicus — live embroidery preview on the product page
 * ===========================================================================
 * Reads the YMQ embroidery fields and shows what the customer is typing, on a
 * photograph of a cap, at the size and in the position they have chosen, with
 * the width in centimetres against what that position will take.
 *
 * READ-ONLY, on purpose. It never sets, clears or submits a YMQ field. Same
 * rule as assets/mc-fit-check.js: we show, the customer chooses. If you are
 * adding a write path, that is a decision to take with Matt, not a detail.
 *
 * IT FINDS FIELDS BY LABEL, NEVER BY ID. The embroidery set is `tem537` on the
 * patterned caps, but the same options are duplicated into other sets with
 * different ids, and ids change when a field is rebuilt. Labels are what the
 * customer reads and what the order line carries, so they are the stable hook:
 *
 *     Text #1 To Embroider - Line 2
 *     #1 Standard Embroidery Text Size Line 2      <- per line, added Sept 2026
 *     #1 Outlined Embroidery Text Font
 *     #2 Embroidery Text Placement
 *
 * Standard and Outlined are two variants of the same text block and YMQ hides
 * whichever is not in play, so the preview follows the VISIBLE one.
 *
 * If nothing matches — a listing without embroidery, or a set whose labels have
 * been renamed — it does nothing at all and leaves the page as it was.
 * =========================================================================== */
(function () {
  'use strict';

  var M = window.MCEmbroidery;
  if (!M) return;

  var RX = {
    line:   /^Text\s*#(\d+)\s*To\s*Embroider\s*-\s*Line\s*(\d+)\s*$/i,
    size:   /^#(\d+)\s+(?:(Standard|Outlined)\s+)?Embroidery\s+Text\s+Size(?:\s+Line\s+(\d+))?\s*$/i,
    font:   /^#(\d+)\s+(?:(Standard|Outlined)\s+)?Embroidery\s+Text\s+Font\s*$/i,
    place:  /^#(\d+)\s+(?:(Standard|Outlined)\s+)?Embroidery\s+Text\s+Placement\s*$/i,
    colour: /^#(\d+)\s+(?:Main\s+)?Embroidery\s+Text\s+Colours?\s*$/i
  };

  function boxes() {
    return Array.prototype.slice.call(
      document.querySelectorAll('.ymq-options-box[ymq-option-label]'));
  }
  function labelOf(b) { return (b.getAttribute('ymq-option-label') || '').trim(); }
  function visible(b) {
    return !!(b && (b.offsetWidth || b.offsetHeight || b.getClientRects().length));
  }

  /* --- reading a field ---------------------------------------------------- */

  function value(b) {
    if (!b) return '';
    var sel = b.querySelector('select');
    if (sel) return sel.value || '';
    var txt = b.querySelector('input.ymq-input-text, input[type="text"], textarea');
    if (txt) return txt.value || '';
    var r = b.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
    if (r) return r.value || '';
    return '';
  }

  /* "Size 2 (1.2 cm High) Popular" → { n: 2, mm: 12 }. The cm figure is read
     from the label itself rather than from a table here, so if the sizes are
     ever re-cut in the app this follows without a code change. The labels are
     inconsistent about the space before "cm" — hence the \s*. */
  function sizeOf(text) {
    if (!text) return null;
    if (/big as possible/i.test(text)) return { n: 'Big', mm: 20, big: true };
    var cm = text.match(/([\d.]+)\s*cm/i);
    var n  = text.match(/size\s*(\d+)/i);
    if (cm) return { n: n ? +n[1] : null, mm: parseFloat(cm[1]) * 10 };
    if (n) {
      for (var i = 0; i < M.SIZES.length; i++) if (M.SIZES[i].n === +n[1]) return M.SIZES[i];
    }
    return null;
  }

  function placeOf(text) {
    if (/front/i.test(text)) return 'front';
    if (/left/i.test(text)) return 'left';
    if (/right/i.test(text)) return 'right';
    if (/top|back/i.test(text)) return 'back';
    return 'front';
  }

  /* The thread colour, sampled from YMQ's own swatch image so the preview is in
     the colour the customer picked rather than a generic white. Shopify's CDN
     sends CORS headers, so the canvas stays readable. Anything that fails falls
     back to off-white, which is what most people order. */
  var _swatch = {};
  function colourOf(b, cb) {
    var v = value(b);
    if (!v) return cb('#f2f3f5');
    if (/^white$/i.test(v)) return cb('#f4f5f7');
    if (/^black$/i.test(v)) return cb('#1b1b1e');
    if (_swatch[v]) return cb(_swatch[v]);
    var item = b.querySelector('input[type="radio"]:checked');
    item = item && item.closest('.ymq-option-value-item');
    var img = item && Array.prototype.slice.call(item.querySelectorAll('img'))
      .filter(function (i) { return !/Tick/i.test(i.src); })[0];
    if (!img) return cb('#f2f3f5');
    var probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onload = function () {
      try {
        var cv = document.createElement('canvas');
        cv.width = cv.height = 8;
        var c = cv.getContext('2d');
        c.drawImage(probe, 0, 0, 8, 8);
        var d = c.getImageData(3, 3, 2, 2).data;
        _swatch[v] = 'rgb(' + d[0] + ',' + d[1] + ',' + d[2] + ')';
        cb(_swatch[v]);
      } catch (e) { cb('#f2f3f5'); }
    };
    probe.onerror = function () { cb('#f2f3f5'); };
    probe.src = img.src;
  }

  /* --- grouping ----------------------------------------------------------- */

  function collect() {
    var groups = {};
    function g(n) {
      if (!groups[n]) groups[n] = { n: n, lines: {}, sizes: {}, font: null, place: null, colour: null };
      return groups[n];
    }
    boxes().forEach(function (b) {
      var L = labelOf(b), m;
      if ((m = L.match(RX.line)))   { g(m[1]).lines[m[2]] = b; return; }
      if ((m = L.match(RX.size)))   {
        if (!visible(b)) return;
        g(m[1]).sizes[m[3] || 'all'] = b; return;
      }
      if ((m = L.match(RX.font)))   { if (visible(b)) g(m[1]).font = b; return; }
      if ((m = L.match(RX.place)))  { if (visible(b)) g(m[1]).place = b; return; }
      if ((m = L.match(RX.colour))) { if (visible(b)) g(m[1]).colour = b; return; }
    });
    return groups;
  }

  function stateOf(gr) {
    var lines = [], any = false;
    for (var i = 1; i <= 6; i++) {
      var lb = gr.lines[i];
      if (!lb || !visible(lb)) continue;
      var t = value(lb);
      var sb = gr.sizes[i] || gr.sizes.all;
      var sz = sizeOf(value(sb)) || { n: 2, mm: 12 };
      if (t && t.trim()) any = true;
      lines.push({ text: t, size: sz.n, sizeMm: sz.mm });
    }
    if (!lines.length) return null;
    var first = gr.sizes[1] || gr.sizes.all;
    var fsz = sizeOf(value(first)) || { n: 2, mm: 12 };
    return {
      any: any,
      lines: lines,
      font: M.normaliseFont(value(gr.font)),
      size: fsz.n, sizeMm: fsz.mm,
      placement: placeOf(value(gr.place))
    };
  }

  /* --- mounting -----------------------------------------------------------
     THE PREVIEW IS A BUTTON, NOT A PANEL (4 Sept 2026, Matt).

     The cap picture used to sit open in the option column, where at full width
     it pushed the size and colour fields off a phone screen. It is a reference,
     not a step, so what stays in the column is one line: a button carrying the
     widest line's width in centimetres and the verdict pill. The picture, the
     meter and the sentence open in a lightbox over the page, and the widget
     itself is only mounted the first time it is opened — until then this costs
     one M.fit() per keystroke and no canvas at all.

     Still read-only. Nothing here writes to a YMQ field.
     ---------------------------------------------------------------------- */

  var mounted = {};

  /* Every cap photograph is 900 x 746. Used only until the canvas has drawn
     once and can be asked for its own ratio. */
  var THUMB_AR = 900 / 746;

  function copy() { return (window.MCEmbroideryConfig && window.MCEmbroideryConfig.copy) || {}; }

  function noteHtml() {
    var C = copy();
    return (C.note ||
      'An estimate, drawn on a medium/large cap — fabric stretches and a ' +
      'smaller cap has less flat area. Every design is checked by hand before ' +
      'it is stitched, and we will come back to you if we do not think it will ' +
      'look its best.') +
      (window.MCEmbroideryConfig && window.MCEmbroideryConfig.guideUrl
        ? ' <a href="' + window.MCEmbroideryConfig.guideUrl + '">See every size side by side</a>.' : '');
  }

  function filled(st) {
    return (st.lines || []).filter(function (l) { return l.text && l.text.trim(); });
  }

  /* The widest line decides, exactly as it does inside the widget: it is the one
     that either fits the panel or does not. */
  function widest(st) {
    var ls = filled(st), best = null, bw = -1;
    for (var i = 0; i < ls.length; i++) {
      var w = M.widthCm(ls[i].text, st.font, ls[i].sizeMm);
      if (w > bw) { bw = w; best = ls[i]; }
    }
    return best ? { line: best, cm: bw } : null;
  }

  /* What the closed button says. Same source as the picture — M.fit() and
     M.fitsHeight() — so the two can never disagree. */
  function readout(st) {
    var w = widest(st);
    if (!w) return { verdict: 'empty', pill: '', read: 'Type your text and see it on a cap' };
    var f = M.fit(w.line.text, st.font, w.line.sizeMm, st.placement);
    var tall = M.fitsHeight(filled(st), st.placement);
    var over = f.verdict === 'over' || !tall.ok;
    return {
      verdict: over ? 'over' : f.verdict,
      pill: f.verdict === 'over' ? 'Too wide' : !tall.ok ? 'Too tall'
          : f.verdict === 'tight' ? 'Tight' : 'Comfortable',
      /* Number first: on a phone this line is what gets truncated, and the
         width is the part worth keeping. */
      read: 'About ' + (Math.round(f.cm * 10) / 10).toFixed(1) + ' cm wide at Size ' +
            w.line.size + ' — “' + w.line.text + '”'
    };
  }

  /* --- the lightbox ------------------------------------------------------- */

  var openRec = null, scrollLock = '';

  function lock() {
    scrollLock = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
  }
  function unlock() { document.documentElement.style.overflow = scrollLock; }

  function openBox(rec) {
    if (openRec === rec) return;
    if (openRec) closeBox();
    openRec = rec;
    rec.modal.hidden = false;
    lock();
    /* Mounted on first open only: no canvas work for a customer who never asks. */
    if (!rec.api) rec.api = M.mount(rec.inner, rec.state);
    else rec.api.update(rec.state);
    rec.btn.setAttribute('aria-expanded', 'true');
    try { rec.close.focus(); } catch (e) {}
  }

  function closeBox() {
    var rec = openRec;
    if (!rec) return;
    openRec = null;
    rec.modal.hidden = true;
    unlock();
    rec.btn.setAttribute('aria-expanded', 'false');
    try { rec.btn.focus(); } catch (e) {}
  }

  /* Esc closes, and Tab stays inside the dialog while it is open. */
  document.addEventListener('keydown', function (e) {
    if (!openRec) return;
    if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); return closeBox(); }
    if (e.key !== 'Tab') return;
    var f = Array.prototype.slice.call(openRec.panel.querySelectorAll(
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'))
      .filter(function (n) { return n.offsetWidth || n.offsetHeight || n.getClientRects().length; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }, true);

  /* --- the mini cap on the button ----------------------------------------
     The same widget the lightbox uses, mounted `compact` (picture only) into a
     108 x 72 window and cropped to the placement: the front takes a 16 cm view,
     the top-at-back an 11 cm one, so the text is a readable size in both rather
     than lost in the middle of a whole cap. Nothing here is hand-tuned — the
     crop is worked out from that placement's own maximum width and pixels-per-
     centimetre, so it follows if either is ever corrected.
     ---------------------------------------------------------------------- */
  function placeThumb(rec) {
    var cv = rec.thumb.querySelector('canvas');
    if (!cv) return;
    var p = M.PLACEMENTS[rec.state.placement] || M.PLACEMENTS.front;
    var W = rec.thumb.clientWidth, H = rec.thumb.clientHeight;
    if (!W || !H) return;
    var ar = (cv.width && cv.height) ? cv.width / cv.height : THUMB_AR;
    var cover = (p.maxCm || 13) + 3;                 /* cm of cap across the window */
    var cw = W / (cover * p.ppcm), ch = cw / ar;
    cv.style.width = cw + 'px';
    cv.style.height = ch + 'px';
    cv.style.left = (W / 2 - (p.cx || 0.5) * cw) + 'px';
    cv.style.top = (H / 2 - ((p.seamY || 0.72) - 0.09) * ch) + 'px';
  }
  /* The picture is drawn asynchronously (photo, then webfont), so the crop is
     re-applied a couple of times rather than guessed once. */
  function bumpThumb(rec) {
    [0, 220, 900].forEach(function (ms) { setTimeout(function () { placeThumb(rec); }, ms); });
  }

  function build(n, st) {
    var C = copy();
    var title = C.title || 'Roughly how your embroidery will look';
    var label = C.cue || 'Preview your embroidery text';

    var host = document.createElement('div');
    host.className = 'mc-emb-cue';
    host.setAttribute('data-mc-emb-group', n);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'mc-emb-cue__btn';
    btn.setAttribute('aria-haspopup', 'dialog');
    btn.setAttribute('aria-expanded', 'false');
    btn.innerHTML =
      '<span class="mc-emb-cue__thumb" aria-hidden="true"></span>' +
      '<span class="mc-emb-cue__text"><b class="mc-emb-cue__label"></b>' +
      '<i class="mc-emb-cue__read"></i></span>' +
      '<span class="mc-emb-cue__pill"></span>';
    btn.querySelector('.mc-emb-cue__label').textContent = label;
    host.appendChild(btn);

    var modal = document.createElement('div');
    modal.className = 'mc-emb-modal';
    modal.hidden = true;

    var scrim = document.createElement('div');
    scrim.className = 'mc-emb-modal__scrim';

    var panel = document.createElement('div');
    panel.className = 'mc-emb-modal__panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-label', title);

    var head = document.createElement('div');
    head.className = 'mc-emb-modal__head';
    var h = document.createElement('p');
    h.className = 'mc-emb-modal__title';
    h.textContent = title;
    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'mc-emb-modal__x';
    close.setAttribute('aria-label', 'Close preview');
    close.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
    head.appendChild(h); head.appendChild(close);

    var body = document.createElement('div');
    body.className = 'mc-emb-modal__body';
    var inner = document.createElement('div');
    var note = document.createElement('p');
    note.className = 'mc-emb__note';
    note.innerHTML = noteHtml();
    body.appendChild(inner); body.appendChild(note);

    panel.appendChild(head); panel.appendChild(body);
    modal.appendChild(scrim); modal.appendChild(panel);
    /* On the body, not in the option column: YMQ's own wrappers clip and
       transform, and a fixed child of a transformed parent is not fixed. */
    document.body.appendChild(modal);

    var thumb = btn.querySelector('.mc-emb-cue__thumb');
    var thumbInner = document.createElement('span');
    thumb.appendChild(thumbInner);

    var rec = {
      host: host, btn: btn, modal: modal, panel: panel, close: close,
      inner: inner, api: null, state: st, thumb: thumb,
      thumbApi: M.mount(thumbInner, {
        lines: st.lines, font: st.font, size: st.size, sizeMm: st.sizeMm,
        placement: st.placement, colour: st.colour, compact: true
      }),
      label: btn.querySelector('.mc-emb-cue__read'),
      pill: btn.querySelector('.mc-emb-cue__pill')
    };

    btn.addEventListener('click', function () { openBox(rec); });
    close.addEventListener('click', closeBox);
    scrim.addEventListener('click', closeBox);
    window.addEventListener('resize', function () { placeThumb(rec); });
    bumpThumb(rec);

    return rec;
  }

  function anchorFor(gr) {
    if (gr.place && visible(gr.place)) return gr.place;
    var keys = Object.keys(gr.sizes);
    for (var i = keys.length - 1; i >= 0; i--) {
      if (visible(gr.sizes[keys[i]])) return gr.sizes[keys[i]];
    }
    return null;
  }

  function ensure(n, gr, st) {
    var rec = mounted[n];
    if (!rec) rec = mounted[n] = build(n, st);
    var anchor = anchorFor(gr);
    if (anchor && rec.host.previousElementSibling !== anchor) {
      anchor.parentNode.insertBefore(rec.host, anchor.nextSibling);
    }
    if (rec.host.hidden) rec.host.hidden = false;      /* guarded: see apply() */
    return rec;
  }

  function apply(n, gr, st) {
    var rec = ensure(n, gr, st);
    rec.seen = true;
    colourOf(gr.colour, function (col) {
      rec.state = {
        lines: st.lines, font: st.font, size: st.size,
        sizeMm: st.sizeMm, placement: st.placement, colour: col
      };
      /* ⚠️ WRITE ONLY WHAT CHANGED. The observer below watches document.body,
         so an unconditional textContent assignment here would mutate the DOM,
         wake the observer, and come straight back round — a loop that never
         settles. Every write in this function is guarded for that reason. */
      var r = readout(rec.state);
      if (rec.host.getAttribute('data-verdict') !== r.verdict) {
        rec.host.setAttribute('data-verdict', r.verdict);
      }
      if (rec.label.textContent !== r.read) rec.label.textContent = r.read;
      if (rec.pill.textContent !== r.pill) rec.pill.textContent = r.pill;
      if (rec.thumbApi.update(rec.state)) bumpThumb(rec);
      /* The big picture is only redrawn while somebody is looking at it. */
      if (rec.api && openRec === rec) rec.api.update(rec.state);
    });
  }

  function refresh() {
    var groups = collect(), n;
    for (n in mounted) mounted[n].seen = false;
    for (n in groups) {
      var st = groups[n].font ? stateOf(groups[n]) : null;
      if (st) apply(n, groups[n], st);
    }
    for (n in mounted) if (!mounted[n].seen) {
      /* Guarded: setting an attribute to the value it already has still wakes
         the observer, and the observer calls back into here. */
      if (!mounted[n].host.hidden) mounted[n].host.hidden = true;
      if (openRec === mounted[n]) closeBox();
    }
  }


  /* INP fix (7 Sept 2026). YMQ lazy-loads ~930 swatch images over the first
     minute and flips a class on each one as it lands; with five scripts
     watching document.body that was 24s of blocked main thread in the first
     33s of every product page (GSC: product group INP 490ms from 4 Sept).
     Only container-level changes matter to this script; value-level class
     flips, images, the header search box and our own widgets are ignored. */
  var MC_IGNORE_ATTR = '.ymq-option-value,.ymq-option-value-item,.ymq-option-swatches-item,.ymq-option-label,.ymq-option-button-input,img,input,textarea,.search-mega-menu__input';
  var MC_OWN = '[data-mc-fit-card],.mc-fit__check,.mc-emb-cue,.mc-emb-modal,[data-mc-satc],.mc-tellus-box,.mc-terms-box';
  function mcRelevant(records) {
    for (var i = 0; i < records.length; i++) {
      var m = records[i], t = m.target;
      if (t.nodeType !== 1) { t = t.parentElement; if (!t) continue; }
      if (t.closest && t.closest(MC_OWN)) continue;
      if (m.type === 'attributes' && t.matches && t.matches(MC_IGNORE_ATTR)) continue;
      return true;
    }
    return false;
  }

  var t = null;
  function bump() { clearTimeout(t); t = setTimeout(refresh, 150); }
  function bumpIf(records) { if (mcRelevant(records)) bump(); }

  function start() {
    refresh();
    document.addEventListener('input', bump, true);
    document.addEventListener('change', bump, true);
    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('.ymq-options-box')) setTimeout(bump, 120);
    }, true);
    /* YMQ shows and hides fields by toggling a class, which fires no event.
       ⚠️ Watch document.body, nothing narrower. This used to fall back to
       `document.querySelector('form')`, which on this theme is the HEADER
       SEARCH form: no option ever mutates inside it, so when YMQ hid the
       embroidery fields after its first paint nothing told the preview to go
       away, and an empty cue sat on the page for a customer who had not asked
       for embroidery at all. (4 Sept 2026) */
    new MutationObserver(bumpIf).observe(document.body, {
      subtree: true, childList: true,
      attributes: true, attributeFilter: ['class', 'style', 'value']
    });
  }

  /* YMQ renders late. Wait for the embroidery labels to exist, then give up. */
  var tries = 0;
  (function wait() {
    if (document.querySelector('.ymq-options-box[ymq-option-label*="Embroider"]')) return start();
    if (++tries > 40) return;
    setTimeout(wait, 400);
  })();
})();
